/**
 * The chrome over the kitchen: order dockets, the score, the round clock.
 *
 * Built around one idea from the reference screenshot: **an order ticket is a
 * recipe card.** It shows the finished dish,
 * the ingredients that go into it, and the vessel they need. A player never has
 * to know a recipe, because the ticket teaches it — which was the actual reason
 * nobody could work out how to make a vegetable stew.
 *
 * The HUD is two rails that flank the kitchen, rather than dockets floating
 * over the top-left corner of the canvas, so the sides of a wide frame have a
 * job.
 *
 *   left   the pass — the order dockets on a rail, score at the foot
 *   right  the round — the brigade, the keys, the clock at the foot
 *
 * The reference's spatial logic survives it: score bottom-left, clock
 * bottom-right, dockets in a queue that reads from the top, and the middle of
 * the frame left alone for the kitchen. The two numbers you glance at are still
 * in fixed corners, so glancing costs no search — they just sit on a surface
 * now instead of over the picture.
 *
 * A docket leads with the *plated dish* and then lists what goes into it. "What
 * am I aiming at" is a different question from "what do I fetch", and a player
 * who has never seen a roast platter cannot answer the first from a list of
 * vegetables.
 *
 * The React/canvas split is the
 * reason this file looks the way it does:
 *
 *   - continuous values — every ticket's fuse, its urgency step, the round
 *     clock — are recomputed on one animation frame and written straight to the
 *     DOM through refs. They never touch reconciliation. The urgency *step* is
 *     written as a `data-urgency` attribute so CSS can change the card's shape
 *     and motion without React knowing a fuse moved.
 *   - discrete changes — a ticket appearing or leaving, the score moving — go
 *     through `setState`, gated on `hudSignature`.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { StationType } from '@overcooked/shared'
import type { EventFeed } from '../feed/feed'
import type { Catalog } from './catalog'
import { EMPTY_CATALOG } from './catalog'
import { BonusChip } from './BonusChip'
import { IconCanvas } from './IconCanvas'
import { drawContainer, drawIngredient, drawPlate, drawStationBadge } from './icons'
import { drawChefBadge } from './players'
import { seatIdentity } from './theme'
import type { HudModel, SeatRow, Ticket } from './hudState'
import { deriveHud, formatClock, roundClock, ticketFuse, urgencyColour } from './hudState'
import { refusalFor } from './reasons'
import type { RecipesControl } from './RecipePanel'
import { RecipePanel } from './RecipePanel'
import { RouteIcon, stationName } from './RouteIcon'
import { routeKey, routesFor } from './route'

const EMPTY: HudModel = {
  tickets: [],
  seats: [],
  score: 0,
  roundDurationTicks: 0,
  orderCues: [],
  refusal: null,
  signature: '',
}

interface HudProps {
  feed: EventFeed
  catalog?: Catalog
  /** Whose row in the roster says "you". `null` on the replay page. */
  selfId?: string | null
  /**
   * The recipe panel, if this page has one. Owned by the page because the key
   * that toggles it is bound there, beside the movement keys. Absent on
   * `/replay`, which has no player to answer "what do I do next?" for.
   */
  recipes?: RecipesControl | null
}

export function Hud({ feed, catalog = EMPTY_CATALOG, selfId = null, recipes = null }: HudProps) {
  const [model, setModel] = useState<HudModel>(EMPTY)
  const dishes = useMemo(() => catalog.dishes(), [catalog])

  // Discrete half. `signature` is the gate: a fuse burning down changes the
  // snapshot on every frame and must not reach React.
  const signatureRef = useRef('')
  useEffect(() => {
    const update = (): void => {
      const next = deriveHud(feed.snapshot(), dishes)
      if (next.signature === signatureRef.current) return
      signatureRef.current = next.signature
      setModel(next)
    }
    update()
    return feed.subscribe(update)
  }, [feed, dishes])

  // Continuous half. One rAF loop for the whole HUD, writing widths, colours,
  // urgency steps and the clock text into elements it already owns.
  const clockRef = useRef<HTMLSpanElement>(null)
  const clockFillRef = useRef<HTMLDivElement>(null)
  const clockBadgeRef = useRef<HTMLDivElement>(null)
  const fuseRefs = useRef(new Map<string, HTMLDivElement>())
  const cardRefs = useRef(new Map<string, HTMLLIElement>())

  useEffect(() => {
    let raf = 0
    const frame = (): void => {
      const snapshot = feed.snapshot()
      const state = snapshot.state
      if (state) {
        // `snapshot.ended === null` is the round still running, and it is what
        // keeps the badge from reading 0:00 while there is still a kitchen to
        // cook in: the tick is an estimate and the end is a fact.
        const clock = roundClock(snapshot.tick, state.roundDurationTicks, snapshot.ended === null)
        if (clockRef.current) clockRef.current.textContent = clock.label
        if (clockBadgeRef.current) clockBadgeRef.current.dataset.urgency = clock.urgency
        if (clockFillRef.current) {
          clockFillRef.current.style.transform = `scaleX(${clock.fraction})`
          clockFillRef.current.style.background = urgencyColour(clock.urgency)
        }
        for (const order of state.orders) {
          const fuse = ticketFuse(order, snapshot.tick)
          const bar = fuseRefs.current.get(order.id)
          if (bar) {
            bar.style.transform = `scaleX(${fuse.fraction})`
            bar.style.background = fuse.colour
          }
          const card = cardRefs.current.get(order.id)
          if (card && card.dataset.urgency !== fuse.urgency) card.dataset.urgency = fuse.urgency
        }
      }
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [feed])

  const latestCue = model.orderCues[model.orderCues.length - 1]

  return (
    <div className="hud" aria-live="polite">
      {/* The pass rail. Dockets hang down the left edge of the stage, clipped
          to a rail, with the score at its foot — the reference pins its tickets
          to the frame's own edge for the same reason: a card floating over the
          middle of a picture is anchored to nothing. */}
      <section className="rail rail-orders">
        <header className="rail-head">
          <span className="rail-cap">Orders</span>
          <span className="rail-count">{model.tickets.length}</span>
        </header>
        <ol className="ticket-rail" aria-label="Orders">
          {model.tickets.map((ticket, index) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              catalog={catalog}
              // The rail is sorted closest-to-expiry first, so index 0 is by
              // definition the ticket a serve of that dish would satisfy. That
              // makes it the one worth spending the height on.
              expanded={index === 0}
              fuseRef={(el) => {
                if (el) fuseRefs.current.set(ticket.id, el)
                else fuseRefs.current.delete(ticket.id)
              }}
              cardRef={(el) => {
                if (el) cardRefs.current.set(ticket.id, el)
                else cardRefs.current.delete(ticket.id)
              }}
            />
          ))}
          {model.tickets.length === 0 && (
            <li className="ticket ticket-empty">
              <span>Waiting for the first order…</span>
            </li>
          )}
        </ol>

        <div className="badge-score" aria-label={`Score: ${model.score}`}>
          <span className="badge-score-value">{model.score}</span>
          <span className="badge-cap">score</span>
        </div>
      </section>

      {/* The round rail: who is cooking, what the keys do, how long is left. */}
      <section className="rail rail-round">
        <header className="rail-head">
          <span className="rail-cap">Chefs</span>
        </header>
        <ul className="brigade" aria-label="Chefs">
          {model.seats.map((row) => (
            <BrigadeRow key={row.id} row={row} isSelf={row.id === selfId} />
          ))}
        </ul>

        <div className="rail-keys">
          <p className="rail-key">
            <span className="keycluster" aria-hidden="true">
              <kbd>&larr;</kbd>
              <kbd>&uarr;</kbd>
              <kbd>&darr;</kbd>
              <kbd>&rarr;</kbd>
            </span>
            Move{' · '}
            {/* The one rule the whole control scheme runs on, stated. It was
                always true and always drawn — the hold glyph sits on exactly the
                stations that want a hold — but "picking up is a tap and chopping
                is a hold" was never written down anywhere, so a playtester read
                it as two inconsistent controls rather than one rule with two
                halves. One line now: the facing hint the old first line carried
                is the tutorial's job. */}
            <kbd className="kbd-wide">space</kbd>
            Tap to pick up or put down, hold to chop, wash or put out fires.
          </p>
        </div>

        {/* The third key, and the only one with a button behind it. It sits
            outside `.rail-keys` because the keys line is hidden when the rails
            lie down on a narrow screen — and that is exactly the screen where
            the docket's route strip collapses and this panel becomes the only
            answer to "what do I do next?".

            The cap is a capital `R` and reads as a key rather than as a bullet,
            which is the whole of the "pressing R does nothing" report: the
            binding was fine — a browser harness pressed it against the
            pre-fix client and the panel opened — but a lowercase letter in a
            small dark box beside a word looks like decoration, so nobody
            pressed it. `aria-keyshortcuts` says the same thing to a screen
            reader, which the aria-hidden cap never did. */}
        {recipes && (
          <button
            type="button"
            className="rail-recipes"
            onClick={recipes.toggle}
            aria-expanded={recipes.open}
            aria-controls="recipe-panel"
            aria-keyshortcuts="R"
          >
            <kbd aria-hidden="true">R</kbd>
            <span>{recipes.open ? 'Hide recipes' : 'Recipes'}</span>
          </button>
        )}

        <div className="badge-clock" ref={clockBadgeRef} data-urgency="calm">
          <span className="badge-clock-value" ref={clockRef}>
            {formatClock(0)}
          </span>
          <div className="badge-clock-track">
            <div className="badge-clock-fill" ref={clockFillRef} />
          </div>
          <span className="badge-cap">left</span>
        </div>
      </section>

      {/* On demand, over the kitchen and clear of both rails. Mounted only
          while it is open: a closed panel is not a hidden panel, it is nothing,
          and there is no reason to keep two dozen canvases alive behind the
          cooking. */}
      {recipes?.open && <RecipePanel catalog={catalog} dishes={dishes} recipes={recipes} />}

      {/* One line, two sources. A refusal wins while it is fresh: "you need a
          plate first" is an answer to a question the player asked half a second
          ago, and a ticket spawning somewhere else is not. The cue on the cell
          says *where*; this says *why*, in words. */}
      {model.refusal ? (
        <p className="hud-flash hud-flash-refusal" key={`refusal-${model.refusal.shownAt}`} role="status">
          {refusalFor(model.refusal.reason).phrase}
        </p>
      ) : (
        latestCue && (
          <p
            className={`hud-flash hud-flash-${latestCue.kind}`}
            key={`${latestCue.kind}-${latestCue.orderId}-${latestCue.shownAt}`}
          >
            {flashText(latestCue.kind, latestCue.scoreDelta)}
          </p>
        )
      )}
    </div>
  )
}

/**
 * One chef in the roster.
 *
 * Drawn with the same code that draws them on the tile, so the picture beside
 * "you" is literally the avatar in the kitchen. It is the only place the
 * silhouette vocabulary is taught — "the tall hat", "the flat cap" — which is
 * what a pair on a voice call reaches for when neither can say "the red one".
 */
function BrigadeRow({ row, isSelf }: { row: SeatRow; isSelf: boolean }) {
  const seat = seatIdentity(row.seat)
  return (
    <li className={isSelf ? 'brigade-row brigade-self' : 'brigade-row'}>
      <IconCanvas
        className="brigade-avatar"
        size={40}
        signature={`chef-${row.seat}`}
        title={`${seat.label} chef (${seat.silhouette})`}
        draw={(ctx, size) => drawChefBadge(ctx, seat, size)}
      />
      <span className="brigade-name">{isSelf ? 'You' : seat.label}</span>
      <span className="brigade-note">{row.connected ? seat.silhouette : 'disconnected'}</span>
    </li>
  )
}

/**
 * One docket.
 *
 * Reading order, top to bottom: the fuse, so it is the first thing seen; the
 * dish name; what to fetch; what to cook it in. That is the order a player
 * needs the information in — "how long have I got" before "what is it" before
 * "what do I do".
 */
function TicketCard({
  ticket,
  catalog,
  expanded = false,
  fuseRef,
  cardRef,
}: {
  ticket: Ticket
  catalog: Catalog
  /** The most urgent docket shows its route as well as its destination. */
  expanded?: boolean
  fuseRef(el: HTMLDivElement | null): void
  cardRef(el: HTMLLIElement | null): void
}) {
  const items = ticket.requires.items
  const stations = ticket.requires.stations.filter(isShownStation)
  const routes = expanded ? routesFor(ticket.requires) : []

  return (
    <li className={expanded ? 'ticket ticket-lead' : 'ticket'} ref={cardRef} data-urgency="calm">
      {/* The fuse: the full width of the card, thick enough to read from the
          corner of the eye, notched so its length is countable in greyscale. */}
      <div className="ticket-fuse-track">
        <div className="ticket-fuse" ref={fuseRef} />
        <div className="ticket-fuse-notches" aria-hidden="true" />
      </div>

      <span className="ticket-name">
        {ticket.label}
        <BonusChip bonus={ticket.bonus} />
      </span>

      {/* The plated dish, then what goes into it. The reference's dockets lead
          with the finished bowl for a reason: "what am I aiming at" is a
          different question from "what do I fetch", and a player who has never
          seen a roast platter has no way to answer the first one from a list of
          vegetables. Drawn from `contents`, which came from `recipes.json`. */}
      <div className="ticket-dish">
        <IconCanvas
          className="ticket-plate"
          size={56}
          signature={`plate-${ticket.contents.join('+')}`}
          title={`${ticket.label}, plated`}
          draw={(ctx, size) =>
            drawPlate(
              ctx,
              { id: ticket.id, clean: true, contents: ticket.contents },
              size / 2,
              size / 2,
              size * 0.44,
              catalog,
            )
          }
        />
        <div className="ticket-recipe">
          {items.length === 0 && <span className="ticket-unknown">?</span>}
          {items.map((item, i) => (
            <IconCanvas
              key={`${item.id}-${i}`}
              className="ticket-icon"
              size={48}
              signature={`${item.source}-${catalog.ingredient(item.source).shape}`}
              title={catalog.ingredient(item.source).label}
              draw={(ctx, size) =>
                drawIngredient(ctx, catalog.ingredient(item.source), size / 2, size / 2, size * 0.42)
              }
            />
          ))}
        </div>
      </div>

      {/* The route, on the leading docket only. The badge row above says what
          the dish is made of; this says what you do about it — crate, board,
          vessel, heat, plate — which is the step a first-timer is missing and
          the one `HIDDEN_ON_TICKET` deliberately keeps off every other card.
          It is a second representation rather than a reversal of that: the
          compact rows behind this one are untouched. */}
      {routes.length > 0 && (
        <div className="ticket-route">
          <span className="ticket-route-cap">Steps</span>
          {routes.map((route) => (
            <ol className="ticket-route-row" key={routeKey(route)} aria-label="Steps, in order">
              {route.map((step, i) => (
                <li className="ticket-route-step" key={`${routeKey([step])}-${i}`}>
                  {i > 0 && <span className="ticket-route-chevron" aria-hidden="true" />}
                  <RouteIcon step={step} catalog={catalog} />
                </li>
              ))}
            </ol>
          ))}
        </div>
      )}

      {(ticket.requires.containers.length > 0 || stations.length > 0) && (
        <div className="ticket-kit">
          {ticket.requires.containers.map((kind) => (
            <IconCanvas
              key={kind}
              className="ticket-kit-icon"
              size={32}
              signature={`container-${kind}`}
              title={catalog.containerLabel(kind)}
              draw={(ctx, size) =>
                drawContainer(
                  ctx,
                  { id: kind, kind, contents: [], cookState: 'empty', phaseStartedAtTick: null, phaseEndsAtTick: null },
                  size / 2,
                  size / 2,
                  size * 0.4,
                  catalog,
                )
              }
            />
          ))}
          {stations.map((station) => (
            <IconCanvas
              key={station}
              className="ticket-kit-icon"
              size={32}
              signature={`station-${station}`}
              title={stationName(station)}
              draw={(ctx, size) => drawStationBadge(ctx, station, size / 2, size / 2, size * 0.36)}
            />
          ))}
        </div>
      )}

      {/* The urgency mark. A shape, shown only when the ticket is in trouble,
          so the alarm does not depend on seeing the fuse turn red. */}
      <span className="ticket-alarm" aria-hidden="true" />
    </li>
  )
}

/**
 * Which equipment is worth putting on a card.
 *
 * The board is on every ticket in v1 and says nothing by being there; the stove
 * and the oven are the choice a player actually has to make, and getting it
 * wrong costs the ticket. Derived from the station list the server sent, so a
 * new station type in a future map appears here without a code change.
 */
const HIDDEN_ON_TICKET: ReadonlySet<StationType> = new Set<StationType>(['board', 'counter', 'crate'])
const isShownStation = (station: StationType): boolean => !HIDDEN_ON_TICKET.has(station)

function flashText(kind: 'spawned' | 'served' | 'expired', scoreDelta: number): string {
  if (kind === 'spawned') return 'New order'
  if (kind === 'expired') return `Order missed · ${signed(scoreDelta)}`
  return scoreDelta === 0 ? 'Served, but nobody ordered it' : `Order served · ${signed(scoreDelta)}`
}

const signed = (n: number): string => (n > 0 ? `+${n}` : String(n))
